import {
  AttendanceCalendarDay,
  AttendanceCalendarResponse
} from 'services/AttendanceTrackingService';
import { buildAttendanceStatusLabels } from 'utils/formatAttendanceDashboard';
import { buildPresentStatusLabels } from 'utils/attendanceOfficeRules';
import type { DayRecord, DayStatus } from './calendarShared';

const formatTime = (value?: string | null) =>
  value ? value.slice(0, 5) : '—';

export const mapCalendarDayToRecord = (day: AttendanceCalendarDay): DayRecord | null => {
  if (day.kind === 'Weekend') return null;

  if (day.kind === 'PublicHoliday') {
    return {
      date: day.date,
      status: 'PublicHoliday',
      arrival: '—',
      departure: '—',
      hours: '—',
      publicHolidayName: day.publicHolidayName ?? 'Mercantile Holiday'
    };
  }

  if (!day.status) return null;

  return {
    date: day.date,
    status: day.status,
    arrival: formatTime(day.arrivalTime),
    departure: formatTime(day.departureTime),
    hours: day.hoursWorked ?? '—'
  };
};

export const mapCalendarDaysToRecords = (
  days: AttendanceCalendarDay[]
): Map<string, DayRecord> => {
  const map = new Map<string, DayRecord>();

  days.forEach((day) => {
    const record = mapCalendarDayToRecord(day);
    if (record) {
      map.set(record.date, record);
    }
  });

  return map;
};

export const buildCalendarStatusLabels = (
  fullDayHoursThreshold: number,
  shortDayHoursThreshold: number
): Record<DayStatus, string> => {
  const labels = buildAttendanceStatusLabels(
    fullDayHoursThreshold,
    shortDayHoursThreshold
  );

  return {
    Present: labels.Present,
    ShortDay: labels.ShortDay,
    VeryShort: labels.VeryShort,
    Missing: labels.Missing,
    WFH: 'WFH',
    FullDayLeave: labels.FullDayLeave,
    HalfDayLeave: labels.HalfDayLeave,
    PublicHoliday: 'Mercantile Holiday'
  };
};

export const buildCalendarLegendItems = (
  fullDayHoursThreshold: number,
  shortDayHoursThreshold: number
) => {
  const labels = buildPresentStatusLabels(
    fullDayHoursThreshold,
    shortDayHoursThreshold
  );

  return [
    { className: 'present', label: labels.present },
    { className: 'short-day', label: labels.shortDay },
    { className: 'very-short', label: labels.veryShort },
    { className: 'missing', label: labels.missing },
    { className: 'wfh', label: 'WFH' },
    { className: 'full-day-leave', label: labels.fullDayLeave },
    { className: 'half-day-leave', label: labels.halfDayLeave },
    { className: 'public-holiday', label: 'Mercantile Holiday' }
  ];
};

export const getCalendarDefaults = (response: AttendanceCalendarResponse) => ({
  employeeName: response.employeeFullName,
  minimumOfficeDays: response.requiredOfficeDays,
  legendItems: buildCalendarLegendItems(
    response.fullDayHoursThreshold,
    response.shortDayHoursThreshold
  ),
  statusLabels: buildCalendarStatusLabels(
    response.fullDayHoursThreshold,
    response.shortDayHoursThreshold
  )
});
